// New-visit form. Besides the visit itself, it walks the vet through every main parameter of the
// overview (weight, allergies, chronic conditions, vaccinations, prevention, behaviour): each section
// shows what is on record, lets the vet change it, and must be marked as reviewed before sealing.
import { useState, type ReactNode } from "react";
import {
  REVIEW_ITEMS, type ConditionStatus, type NewVisitInput, type PetRecord, type Prescription, type ReviewItem, type Tone,
} from "../../../shared/types";
import { conditionName, fmtDate } from "../../format";
import { currentMedications, medCaution } from "../../meds";

type Vacc = { name: string; product: string; batchNo: string; nextDue: string };
type Rx = { category: Prescription["category"]; name: string; instructions: string; quantity: string; endsOn: string };
type ExtMed = { source: "owner" | "other_clinic"; name: string; instructions: string; quantity: string; startedOn: string };
type Allergy = { category: "drug" | "food" | "medical"; substance: string };
type NewCondition = { name: string; status: ConditionStatus; detail: string };
type Behaviour = { kind: "positive" | "watch" | "note"; headline: string; body: string };
type ConditionUpdate = { status: ConditionStatus | ""; detail: string };

export interface VisitDraft {
  title: string; summary: string; notes: string; tags: string; tone: Tone; weight: string;
  vaccs: Vacc[]; rxs: Rx[]; extMeds: ExtMed[]; allergies: Allergy[];
  conditionUpdates: Record<string, ConditionUpdate>; newConditions: NewCondition[];
  behaviour: Behaviour[];
  reviewed: ReviewItem[];
}

export const emptyDraft = (): VisitDraft => ({
  title: "", summary: "", notes: "", tags: "", tone: "teal", weight: "",
  vaccs: [], rxs: [], extMeds: [], allergies: [], conditionUpdates: {}, newConditions: [], behaviour: [], reviewed: [],
});

export function draftToInput(d: VisitDraft, record: PetRecord): Omit<NewVisitInput, "visitDate"> {
  const updates = record.conditions.flatMap((c) => {
    const u = d.conditionUpdates[c.name];
    if (!u || (!u.status && !u.detail.trim())) return [];
    return [{ name: c.name, status: (u.status || c.status) as ConditionStatus, detail: u.detail.trim() || c.detail, icon: c.icon, tone: c.tone }];
  });
  return {
    title: d.title, summary: d.summary, notes: d.notes,
    weightLb: d.weight ? Number(d.weight) : undefined,
    tags: d.tags.split(",").map((t) => t.trim()).filter(Boolean).map((label) => ({ label, tone: d.tone })),
    vaccinations: d.vaccs.filter((v) => v.name).map((v) => ({ name: v.name, product: v.product || undefined, batchNo: v.batchNo || undefined, nextDue: v.nextDue || undefined })),
    prescriptions: d.rxs.filter((r) => r.name).map((r) => ({ category: r.category, name: r.name, instructions: r.instructions || undefined, quantity: r.quantity || undefined, endsOn: r.endsOn || undefined })),
    externalMeds: d.extMeds.filter((m) => m.name).map((m) => ({ source: m.source, name: m.name, instructions: m.instructions || undefined, quantity: m.quantity || undefined, startedOn: m.startedOn || undefined })),
    allergies: d.allergies.filter((a) => a.substance),
    conditions: [...updates, ...d.newConditions.filter((c) => c.name.trim()).map((c) => ({ name: c.name.trim(), status: c.status, detail: c.detail }))],
    behaviour: d.behaviour.filter((b) => b.body.trim()).map((b) => ({ kind: b.kind, headline: b.headline || undefined, body: b.body })),
    reviewed: d.reviewed,
  };
}

export const allReviewed = (d: VisitDraft) => REVIEW_ITEMS.every((r) => d.reviewed.includes(r.key));

export function VisitFields({ draft, setDraft, record }: { draft: VisitDraft; setDraft: (d: VisitDraft) => void; record: PetRecord }) {
  // Any edit inside a section counts as reviewing it.
  const set = (patch: Partial<VisitDraft>, touched?: ReviewItem) =>
    setDraft({ ...draft, ...patch, reviewed: touched && !draft.reviewed.includes(touched) ? [...draft.reviewed, touched] : (patch.reviewed ?? draft.reviewed) });
  const rows = <K extends "vaccs" | "rxs" | "extMeds" | "allergies" | "newConditions" | "behaviour">(k: K, touched: ReviewItem) => ({
    add: (item: VisitDraft[K][number]) => set({ [k]: [...(draft[k] as VisitDraft[K][number][]), item] } as Partial<VisitDraft>, touched),
    upd: (i: number, patch: Partial<VisitDraft[K][number]>) =>
      set({ [k]: (draft[k] as VisitDraft[K][number][]).map((x, j) => (j === i ? { ...x, ...patch } : x)) } as Partial<VisitDraft>, touched),
    del: (i: number) => set({ [k]: (draft[k] as unknown[]).filter((_, j) => j !== i) } as Partial<VisitDraft>, touched),
  });
  const toggle = (key: ReviewItem) =>
    set({ reviewed: draft.reviewed.includes(key) ? draft.reviewed.filter((k) => k !== key) : [...draft.reviewed, key] });

  const vaccs = rows("vaccs", "vaccinations"), rxs = rows("rxs", "prevention"), ext = rows("extMeds", "prevention"), allergies = rows("allergies", "allergies");
  const conds = rows("newConditions", "conditions"), beh = rows("behaviour", "behaviour");
  const conflicts = [...draft.rxs, ...draft.extMeds].flatMap((m) => {
    const c = m.name ? medCaution(m.name, record.allergies) : null;
    return c ? [`${m.name} — ${c}`] : [];
  });
  const meds = currentMedications(record);
  const lastWeight = record.weights.at(-1);
  const prevention = record.prescriptions.active.filter((p) => p.category === "prevention");
  const clinicBehaviour = record.behaviour.filter((b) => b.kind !== "note").slice(0, 6);
  const done = draft.reviewed.length;

  return (
    <>
      {record.allergies.drug.length > 0 && (
        <div className="allergy-drug" style={{ borderRadius: 8, border: "0.5px solid #EFBABA" }}>
          <div className="all-icon all-icon-red" style={{ width: 26, height: 26, fontSize: 13 }}>⛔</div>
          <div><div className="all-label all-label-red">Drug allergies — do not administer</div><div className="atags">{record.allergies.drug.map((a) => <span key={a} className="atag-red">{a}</span>)}</div></div>
        </div>
      )}
      <div className="field"><label>Title *</label><input value={draft.title} maxLength={120} onChange={(e) => set({ title: e.target.value })} placeholder="e.g. Yearly check-up" /></div>
      <div className="field"><label>Summary (one line, shown in history) *</label><input value={draft.summary} maxLength={300} onChange={(e) => set({ summary: e.target.value })} placeholder="e.g. Healthy — annual vaccines given." /></div>
      <div className="field"><label>Clinical notes</label><textarea value={draft.notes} maxLength={8000} rows={4} onChange={(e) => set({ notes: e.target.value })} /></div>
      <div className="frow">
        <div className="field"><label>Tags (comma separated)</label><input value={draft.tags} onChange={(e) => set({ tags: e.target.value })} placeholder="Thyroid, Labs" /></div>
        <div className="field"><label>Tag colour</label>
          <select value={draft.tone} onChange={(e) => set({ tone: e.target.value as Tone })}>
            <option value="teal">Teal</option><option value="ok">Green — resolved / normal</option><option value="warn">Amber — watch</option><option value="high">Red — critical</option><option value="purple">Purple — labs</option>
          </select>
        </div>
      </div>

      <div className="review-h">
        <b>Main parameters — review each one</b>
        <span className={done === REVIEW_ITEMS.length ? "all" : ""}>{done} / {REVIEW_ITEMS.length} reviewed</span>
      </div>

      <Section k="weight" title="Weight" draft={draft} toggle={toggle}
        current={lastWeight ? `Last: ${lastWeight.lb} lb on ${fmtDate(lastWeight.date)}` : "No weight on record"}>
        <div className="field" style={{ maxWidth: 220 }}><label>Weight today (lb)</label>
          <input type="number" step="0.1" min="0" value={draft.weight} onChange={(e) => set({ weight: e.target.value }, "weight")} placeholder={lastWeight ? String(lastWeight.lb) : ""} />
        </div>
      </Section>

      <Section k="allergies" title="Allergies & sensitivities" draft={draft} toggle={toggle}
        current={[...record.allergies.drug, ...record.allergies.food, ...record.allergies.medical].join(" · ") || "None recorded"}
        onAdd={() => allergies.add({ category: "drug", substance: "" })} addLabel="+ Add allergy">
        {draft.allergies.map((a, i) => (
          <div key={i} className="sub-grid" style={{ gridTemplateColumns: "120px 1fr 28px" }}>
            <div className="field"><select value={a.category} onChange={(e) => allergies.upd(i, { category: e.target.value as Allergy["category"] })}>
              <option value="drug">Drug</option><option value="food">Food</option><option value="medical">Medical</option>
            </select></div>
            <div className="field"><input value={a.substance} onChange={(e) => allergies.upd(i, { substance: e.target.value })} placeholder="Substance" /></div>
            <button type="button" className="x" onClick={() => allergies.del(i)} aria-label="Remove">×</button>
          </div>
        ))}
      </Section>

      <Section k="conditions" title="Chronic conditions" draft={draft} toggle={toggle}
        current={record.conditions.length ? `${record.conditions.length} active` : "None on record"}
        onAdd={() => conds.add({ name: "", status: "active", detail: "" })} addLabel="+ Add condition">
        {record.conditions.map((c) => {
          const u = draft.conditionUpdates[c.name] ?? { status: "", detail: "" };
          const setU = (patch: Partial<ConditionUpdate>) => set({ conditionUpdates: { ...draft.conditionUpdates, [c.name]: { ...u, ...patch } } }, "conditions");
          return (
            <div key={c.name} className="cond-row">
              <div className="cond-name"><span>{c.icon ?? "•"}</span><div><div className="t1">{conditionName(c.name)}</div><div className="t3">{c.status} · since {fmtDate(c.since)}</div></div></div>
              <div className="field"><select value={u.status} onChange={(e) => setU({ status: e.target.value as ConditionUpdate["status"] })}>
                <option value="">No change</option><option value="active">Active</option><option value="suspected">Suspected</option><option value="managed">Managed</option><option value="resolved">Resolved</option>
              </select></div>
              <div className="field"><input value={u.detail} onChange={(e) => setU({ detail: e.target.value })} placeholder="Update detail (optional)" /></div>
            </div>
          );
        })}
        {draft.newConditions.map((c, i) => (
          <div key={i} className="sub-grid" style={{ gridTemplateColumns: "1.2fr 130px 1.4fr 28px" }}>
            <div className="field"><input value={c.name} onChange={(e) => conds.upd(i, { name: e.target.value })} placeholder="New condition" /></div>
            <div className="field"><select value={c.status} onChange={(e) => conds.upd(i, { status: e.target.value as ConditionStatus })}>
              <option value="active">Active</option><option value="suspected">Suspected</option><option value="managed">Managed</option>
            </select></div>
            <div className="field"><input value={c.detail} onChange={(e) => conds.upd(i, { detail: e.target.value })} placeholder="Detail" /></div>
            <button type="button" className="x" onClick={() => conds.del(i)} aria-label="Remove">×</button>
          </div>
        ))}
      </Section>

      <Section k="vaccinations" title="Vaccinations" draft={draft} toggle={toggle}
        current={record.vaccines.overdue.length ? `${record.vaccines.overdue.length} overdue: ${record.vaccines.overdue.map((v) => v.name).join(", ")}` : record.vaccines.upcoming[0] ? `Next: ${record.vaccines.upcoming[0].name} · ${fmtDate(record.vaccines.upcoming[0].due)}` : "Nothing due"}
        warn={record.vaccines.overdue.length > 0}
        onAdd={() => vaccs.add({ name: "", product: "", batchNo: "", nextDue: "" })} addLabel="+ Add vaccination given">
        {draft.vaccs.map((v, i) => (
          <div key={i} className="sub-grid" style={{ gridTemplateColumns: "1.4fr 1fr 1fr 1fr 28px" }}>
            <div className="field"><input value={v.name} onChange={(e) => vaccs.upd(i, { name: e.target.value })} placeholder="Vaccine" /></div>
            <div className="field"><input value={v.product} onChange={(e) => vaccs.upd(i, { product: e.target.value })} placeholder="Product" /></div>
            <div className="field"><input value={v.batchNo} onChange={(e) => vaccs.upd(i, { batchNo: e.target.value })} placeholder="Batch" /></div>
            <div className="field"><input type="date" value={v.nextDue} onChange={(e) => vaccs.upd(i, { nextDue: e.target.value })} title="Next due" /></div>
            <button type="button" className="x" onClick={() => vaccs.del(i)} aria-label="Remove">×</button>
          </div>
        ))}
      </Section>

      <Section k="prevention" title="Medications & prevention" draft={draft} toggle={toggle}
        current={[...meds.map((m) => `${m.caution ? "⚠ " : ""}${m.name}${m.source ? ` (${m.source})` : ""}`), ...prevention.map((p) => p.name)].join(" · ") || "No current medication or prevention"}
        warn={meds.some((m) => m.caution)}
        onAdd={() => rxs.add({ category: "medication", name: "", instructions: "", quantity: "", endsOn: "" })} addLabel="+ Add prescription">
        {meds.length > 0 && (
          <div className="rv-list">
            {meds.map((m) => <div key={m.key}>{m.caution && <span className="med-warn">⚠</span>}<b>{m.name}</b>{m.detail ? ` — ${m.detail}` : ""}{m.source ? <span className="src-pill">{m.source}</span> : null}</div>)}
          </div>
        )}
        {draft.rxs.map((r, i) => (
          <div key={i} className="sub-grid" style={{ gridTemplateColumns: "112px 1.4fr 1.2fr 72px 132px 24px" }}>
            <div className="field"><select value={r.category} onChange={(e) => rxs.upd(i, { category: e.target.value as Rx["category"] })}>
              <option value="medication">Medication</option><option value="prevention">Prevention</option><option value="diet">Diet</option><option value="topical">Topical</option>
            </select></div>
            <div className="field"><input value={r.name} onChange={(e) => rxs.upd(i, { name: e.target.value })} placeholder="Drug + strength (e.g. Carprofen 75 mg)" /></div>
            <div className="field"><input value={r.instructions} onChange={(e) => rxs.upd(i, { instructions: e.target.value })} placeholder="Dose & frequency" /></div>
            <div className="field"><input value={r.quantity} onChange={(e) => rxs.upd(i, { quantity: e.target.value })} placeholder="Qty" /></div>
            <div className="field"><input type="date" value={r.endsOn} onChange={(e) => rxs.upd(i, { endsOn: e.target.value })} title="Course ends (empty = ongoing)" /></div>
            <button type="button" className="x" onClick={() => rxs.del(i)} aria-label="Remove">×</button>
          </div>
        ))}
        {draft.extMeds.map((m, i) => (
          <div key={i} className="sub-grid" style={{ gridTemplateColumns: "112px 1.4fr 1.2fr 72px 132px 24px" }}>
            <div className="field"><select value={m.source} onChange={(e) => ext.upd(i, { source: e.target.value as ExtMed["source"] })}>
              <option value="owner">Owner</option><option value="other_clinic">Other clinic</option>
            </select></div>
            <div className="field"><input value={m.name} onChange={(e) => ext.upd(i, { name: e.target.value })} placeholder="Drug + strength (e.g. Gabapentin 100 mg)" /></div>
            <div className="field"><input value={m.instructions} onChange={(e) => ext.upd(i, { instructions: e.target.value })} placeholder="Dose & frequency" /></div>
            <div className="field"><input value={m.quantity} onChange={(e) => ext.upd(i, { quantity: e.target.value })} placeholder="Qty" /></div>
            <div className="field"><input type="date" value={m.startedOn} onChange={(e) => ext.upd(i, { startedOn: e.target.value })} title="Started" /></div>
            <button type="button" className="x" onClick={() => ext.del(i)} aria-label="Remove">×</button>
          </div>
        ))}
        {conflicts.length > 0 && <div className="err">⚠ {conflicts.join(" · ")}</div>}
        <button type="button" className="link" onClick={() => ext.add({ source: "owner", name: "", instructions: "", quantity: "", startedOn: "" })}>+ Add medication from another source (owner / other clinic)</button>
      </Section>

      <Section k="behaviour" title="Behavior" draft={draft} toggle={toggle}
        current={clinicBehaviour.map((b) => `${b.kind === "watch" ? "⚠" : "✓"} ${b.headline ?? b.body}`).join(" · ") || "No observations yet"}
        onAdd={() => beh.add({ kind: "positive", headline: "", body: "" })} addLabel="+ Add observation">
        {draft.behaviour.map((b, i) => (
          <div key={i} className="sub-grid" style={{ gridTemplateColumns: "130px 1fr 1.6fr 28px" }}>
            <div className="field"><select value={b.kind} onChange={(e) => beh.upd(i, { kind: e.target.value as Behaviour["kind"] })}>
              <option value="positive">✓ Positive</option><option value="watch">⚠ Watch</option><option value="note">Note</option>
            </select></div>
            <div className="field"><input value={b.headline} maxLength={80} onChange={(e) => beh.upd(i, { headline: e.target.value })} placeholder="e.g. Calm on the table" /></div>
            <div className="field"><input value={b.body} maxLength={2000} onChange={(e) => beh.upd(i, { body: e.target.value })} placeholder="Observation from this visit" /></div>
            <button type="button" className="x" onClick={() => beh.del(i)} aria-label="Remove">×</button>
          </div>
        ))}
      </Section>
    </>
  );
}

function Section({ k, title, current, warn, draft, toggle, onAdd, addLabel, children }: {
  k: ReviewItem; title: string; current: string; warn?: boolean; draft: VisitDraft; toggle: (k: ReviewItem) => void;
  onAdd?: () => void; addLabel?: string; children?: ReactNode;
}) {
  const reviewed = draft.reviewed.includes(k);
  const [open, setOpen] = useState(false);
  return (
    <div className={`rv-sec${reviewed ? " ok" : ""}`}>
      <div className="rv-top">
        <button type="button" className="rv-title" onClick={() => setOpen(!open)} aria-expanded={open}>
          <span className="rv-dot">{reviewed ? "✓" : ""}</span>
          <div>
            <b>{title}</b>
            <div className={`rv-cur${warn ? " warn" : ""}`}>{current}</div>
          </div>
          <span className="rv-arrow">{open ? "▾" : "▸"}</span>
        </button>
        <label className="rv-check"><input type="checkbox" checked={reviewed} onChange={() => toggle(k)} />Reviewed</label>
      </div>
      {open && (
        <div className="rv-body">
          {children}
          {onAdd && <button type="button" className="link" onClick={onAdd}>{addLabel}</button>}
        </div>
      )}
    </div>
  );
}
